import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND } from "@/lib/brand";
import { trackEvent } from "@/lib/analytics";

const DISMISS_KEY = "pax_pwa_install_dismissed_v1";

/**
 * Soft install-app banner. Uses the standard beforeinstallprompt event so it
 * only shows on browsers/devices that actually support installing this PWA.
 */
export default function PWAInstall() {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let dismissed = false;
        try { dismissed = !!localStorage.getItem(DISMISS_KEY); } catch (e) { void e; }
        const handler = (e) => {
            e.preventDefault();
            if (dismissed) return;
            setDeferred(e);
            setVisible(true);
            trackEvent("pwa_install_prompt_shown");
        };
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        trackEvent("pwa_install_choice", { outcome });
        setVisible(false);
        setDeferred(null);
    };

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch (e) { void e; }
        trackEvent("pwa_install_dismissed");
        setVisible(false);
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ y: 60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 60, opacity: 0 }}
                    className="fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-clayHover rounded-full shadow-clay flex items-center gap-3 pl-4 pr-2 py-2 font-body text-sm"
                    data-testid="pwa-banner"
                >
                    <img src={BRAND.logo} alt="" className="w-5 h-5 object-contain" />
                    <span className="text-ink hidden sm:inline">keep Pax on your home screen?</span>
                    <span className="text-ink sm:hidden">install Pax</span>
                    <button
                        onClick={install}
                        data-testid="pwa-install"
                        className="bg-terracotta text-bone rounded-full px-4 py-1.5 hover:bg-terracottaHover transition-colors flex items-center gap-1.5"
                    >
                        <Download className="w-3.5 h-3.5" /> install
                    </button>
                    <button onClick={dismiss} data-testid="pwa-dismiss" className="text-inkSoft hover:text-ink p-1">
                        <X className="w-4 h-4" />
                    </button>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
